import * as React from 'react';
import { IWebGroupData } from '../../../actions/DataServiceEntities.g';
import { WebGroupLine } from './webgroup-line';
import { ICallerContext } from '@msdyn365-commerce/retail-proxy';
import { IWebGroupCartLine, IWebGroupCartLines } from '../context/webgroup-cart-context';
import { SetFallbackImage } from '../../../utilities/get-images';

export interface IWebGroupGroupProps {
    setState: (state: any) => void;
    webGroup: IWebGroupData;
    image: string;
    actionContext: ICallerContext;
    updateGroup(lineData: IWebGroupCartLines): void;
    handleAddWebGroupsToCart(webgroups: IWebGroupCartLines[]): Promise<boolean>;
    isAuthenticated: boolean;
    searchTerm: string;
}

export const WebGroupGroup: React.FC<IWebGroupGroupProps> = (props: IWebGroupGroupProps) => {
    const [lineData, setLineData] = React.useState<IWebGroupCartLine[]>([]);
    const captionValues = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

    React.useEffect(() => {
        if (lineData.length === 0) {
            const newLineData: IWebGroupCartLine[] = [];
            props.webGroup?.Products?.forEach(product => {
                if (product.ProductValues && product.Dimensions && product.Dimensions?.length > 0) {
                    newLineData.push({ product: product.ProductValues, variant: product.Dimensions[0], quantity: 0, error: false });
                }
            });
            if (props.webGroup.WebGroupHeaders) {
                props.updateGroup({ headers: props.webGroup.WebGroupHeaders, lines: newLineData });
            }
            setLineData(newLineData);
        }
    }, []);

    const readProp = (obj: any, prop: string): string => {
        return obj[`${prop}`];
    };

    const getNumberCaptions = (): number => {
        const captions: string[] = [];
        for (const item of captionValues) {
            const key = `Caption${item}`;
            const caption = readProp(props.webGroup.WebGroupHeaders, key);
            if (caption) {
                captions.push(caption);
            }
        }
        return captions.length;
    };

    const renderTableHeader = (): JSX.Element => {
        const captions: string[] = [];
        for (const item of captionValues) {
            const key = `Caption${item}`;
            const caption = readProp(props.webGroup.WebGroupHeaders, key);
            if (caption) {
                captions.push(caption);
            }
        }
        return (
            <tr>
                <th className='noWrap'>Part Number</th>
                {captions?.map(c => {
                    return <th key={c}>{c}</th>;
                })}
                <th>Configuration (Qty) / Source</th>
                <th>Price/Uom</th>
                <th>Quantity</th>
            </tr>
        );
    };

    const renderTableLines = (): JSX.Element | JSX.Element[] => {
        return (
            props.webGroup?.Products?.map(p => {
                return (
                    <WebGroupLine
                        key={p.ProductValues?.ProductVariant}
                        product={p}
                        actionContext={props.actionContext}
                        updateLine={updateLine}
                        isAuthenticated={props.isAuthenticated}
                        searchTerm={props.searchTerm}
                        bigMode={props.webGroup.Products?.length === 1}
                        headers={props.webGroup.WebGroupHeaders!}
                        forcePropColumns={getNumberCaptions()}
                    />
                );
            }) ?? <tr></tr>
        );
    };

    const renderAddToCart = (): JSX.Element => {
        if (props.isAuthenticated) {
            return (
                <div className='add-to-cart-bar'>
                    <button
                        type='button'
                        className={`add-cart-button ${
                            lineData.some(item => item.quantity !== 0) && !lineData.some(item => item.error) ? '' : 'disabled'
                        }`}
                        onClick={e => props.handleAddWebGroupsToCart([{ headers: props.webGroup.WebGroupHeaders!, lines: lineData }])}
                        disabled={!lineData.some(item => item.quantity !== 0) && !lineData.some(item => item.error)}
                    >
                        Add To Cart
                    </button>
                </div>
            );
        } else {
            return <></>;
        }
    };

    const updateLine = (line: IWebGroupCartLine) => {
        //Because an individual line has been updated, we need to update this line also on the directory
        const newLineData = lineData.slice();
        const updateLine = newLineData.findIndex(i => i.product.ItemId === line.product.ItemId);
        if (updateLine !== -1) {
            newLineData[updateLine] = line;
        }
        if (props.webGroup.WebGroupHeaders) {
            props.updateGroup({ headers: props.webGroup.WebGroupHeaders, lines: newLineData });
        }
        setLineData(newLineData);
    };

    const RenderManyProducts = (): JSX.Element => {
        return (
            <div className='product-details'>
                <div className='product-row'>
                    <div className='left-column'>
                        <div className='product-image'>
                            <img src={props.image} />
                        </div>
                    </div>
                    <div className='right-column'>
                        <div className='product-description'>
                            <h5>Description</h5>
                            <p>{props.webGroup.WebGroupHeaders?.WebDescription}</p>
                        </div>
                    </div>
                </div>
                <div className='table-wrapper'>
                    <table className='list-table'>
                        <thead>{renderTableHeader()}</thead>
                        <tbody>{renderTableLines()}</tbody>
                    </table>
                    {renderAddToCart()}
                </div>
            </div>
        );
    };

    const RenderSingleProduct = (): JSX.Element => {
        if (props.webGroup.Products && props.webGroup.Products.length === 1) {
            const product = props.webGroup.Products[0];
            return (
                <div className='product-details'>
                    <div className='product-row'>
                        <div className='left-column'>
                            <div className='product-image'>
                                <img src={props.image} onError={SetFallbackImage} />
                            </div>
                        </div>
                        <div className='right-column'>
                            <h5 className='noWrap'>Part Number</h5>
                            <p className={props.searchTerm === product.ProductValues?.ItemId ? 'highlight' : ''}>
                                {product?.ProductValues?.ItemId}
                            </p>
                            <h5>Description</h5>
                            <p>{props.webGroup.WebGroupHeaders?.WebDescription}</p>
                            {renderTableLines()}
                        </div>
                    </div>
                    {renderAddToCart()}
                </div>
            );
        } else {
            return <></>;
        }
    };

    return <div>{props.webGroup.Products?.length === 1 ? RenderSingleProduct() : RenderManyProducts()}</div>;
};
